import { useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ShieldCheck, Delete, Check } from 'lucide-react';
import * as dbOps from './db/kioskDbOperations';

/**
 * Post-pairing step to set up a 6-digit admin access code.
 * This code is required to access the admin panel on the kiosk.
 */
export default function KioskSetupAdminCode({ onComplete }) {
  const [step, setStep] = useState('enter'); // 'enter' | 'confirm'
  const [code, setCode] = useState('');
  const [confirmCode, setConfirmCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const activeCode = step === 'enter' ? code : confirmCode;
  const setActiveCode = step === 'enter' ? setCode : setConfirmCode;

  const handleDigit = useCallback((digit) => {
    setError('');
    setActiveCode((prev) => (prev.length >= 6 ? prev : prev + digit));
  }, [setActiveCode]);

  const handleDelete = useCallback(() => {
    setActiveCode((prev) => prev.slice(0, -1));
    setError('');
  }, [setActiveCode]);

  const handleClear = useCallback(() => {
    setActiveCode('');
    setError('');
  }, [setActiveCode]);

  const handleNext = useCallback(async () => {
    if (step === 'enter') {
      if (code.length < 4) {
        setError('Code must be at least 4 digits');
        return;
      }
      setStep('confirm');
      return;
    }

    // Confirm step
    if (confirmCode !== code) {
      setError('Codes do not match. Try again.');
      setConfirmCode('');
      return;
    }

    setSaving(true);
    await dbOps.setConfig('admin_code', code);
    setSaving(false);
    onComplete?.();
  }, [step, code, confirmCode, onComplete]);

  const handleSkip = useCallback(async () => {
    // No admin code — anyone can access admin panel
    onComplete?.();
  }, [onComplete]);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', 'DEL'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center px-6 py-12 select-none"
    >
      <div className="w-full max-w-sm flex flex-col items-center gap-8">
        {/* Icon + Title */}
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-slate-900 flex items-center justify-center shadow-lg">
            <ShieldCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-800">
            {step === 'enter' ? 'Set Admin Code' : 'Confirm Admin Code'}
          </h1>
          <p className="text-base text-slate-500 max-w-xs leading-relaxed">
            {step === 'enter'
              ? 'Set a code to protect admin settings on this kiosk'
              : 'Enter the same code again to confirm'}
          </p>
        </div>

        {/* Code dots */}
        <div className="flex justify-center gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full transition-colors ${
                i < activeCode.length ? 'bg-slate-800' : 'bg-slate-200'
              }`}
            />
          ))}
        </div>

        {/* Error */}
        {error && (
          <p className="text-center text-sm text-red-500 font-medium">{error}</p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 w-full">
          {digits.map((d) => {
            if (d === 'CLR') {
              return (
                <button
                  key={d}
                  onClick={handleClear}
                  className="h-16 rounded-xl bg-slate-100 text-slate-500 text-sm font-semibold active:bg-slate-200 transition-colors"
                >
                  CLR
                </button>
              );
            }
            if (d === 'DEL') {
              return (
                <button
                  key={d}
                  onClick={handleDelete}
                  className="h-16 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center active:bg-slate-200 transition-colors"
                >
                  <Delete className="w-5 h-5" />
                </button>
              );
            }
            return (
              <button
                key={d}
                onClick={() => handleDigit(d)}
                className="h-16 rounded-xl bg-slate-50 text-slate-800 text-2xl font-semibold hover:bg-slate-100 active:bg-slate-200 transition-colors"
              >
                {d}
              </button>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="w-full flex flex-col gap-3">
          <Button
            onClick={handleNext}
            disabled={activeCode.length < 4 || saving}
            className="w-full h-14 text-lg font-semibold rounded-xl"
          >
            {saving ? 'Saving...' : step === 'enter' ? 'Next' : (
              <span className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Confirm & Continue
              </span>
            )}
          </Button>

          {step === 'enter' && (
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="w-full h-12 text-base text-slate-400 hover:text-slate-600"
            >
              Skip for now
            </Button>
          )}

          {step === 'confirm' && (
            <Button
              variant="ghost"
              onClick={() => { setStep('enter'); setCode(''); setConfirmCode(''); setError(''); }}
              className="w-full h-12 text-base text-slate-400 hover:text-slate-600"
            >
              Start over
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
